import { Info } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { sameBrand } from '@/lib/competitors'
import type { ShareOfVoiceProps } from './ShareOfVoice.types'

const SLICE_VARS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
  'var(--chart-7)',
  'var(--chart-8)',
]

export function ShareOfVoice({ scores, trackedBrand }: ShareOfVoiceProps) {
  const items = scores.shareOfVoice ?? []

  if (items.length === 0) {
    return (
      <div className="p-6 rounded-xl border border-border bg-card">
        <h3 className="text-sm font-semibold mb-1">Share of Voice</h3>
        <p className="text-sm text-muted-foreground py-6">
          No mentions detected.
        </p>
      </div>
    )
  }

  return (
    <div className="flex h-full max-h-80 flex-col rounded-xl border border-border bg-card p-6">
      <h3 className="text-sm font-semibold mb-1">Share of Voice</h3>
      <p className="text-xs text-muted-foreground mb-5">
        How mentions split across all responses
      </p>

      {/* Single segmented bar: each brand's slice width = its share. */}
      <div className="flex h-3 rounded-full overflow-hidden bg-secondary mb-5">
        {items.map((it, i) => (
          <div
            key={it.name}
            className="transition-all"
            style={{
              width: `${it.percent}%`,
              background: SLICE_VARS[i % SLICE_VARS.length],
            }}
            title={`${it.name}: ${it.percent.toFixed(1)}%`}
          />
        ))}
      </div>

      <TooltipProvider delayDuration={150}>
        <section
          aria-label="Share of Voice brands"
          className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-2"
        >
          {items.map((it, i) => {
            const isTracked =
              trackedBrand !== undefined && sameBrand(it.name, trackedBrand)

            return (
              <div
                key={it.name}
                className="flex items-center justify-between text-sm"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    className="size-2.5 flex-shrink-0 rounded-sm"
                    style={{ background: SLICE_VARS[i % SLICE_VARS.length] }}
                  />
                  <span
                    className={`truncate ${isTracked ? 'font-semibold' : ''}`}
                  >
                    {it.name}
                  </span>
                  {it.name === 'Unattributed' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="About unattributed mentions"
                          className="flex-shrink-0 cursor-help text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <Info className="size-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-64 leading-relaxed">
                        Mentions we could not match to a known brand.
                      </TooltipContent>
                    </Tooltip>
                  )}
                </span>
                <span className="ml-2 tabular-nums text-muted-foreground">
                  {it.percent.toFixed(1)}%
                </span>
              </div>
            )
          })}
        </section>
      </TooltipProvider>
    </div>
  )
}
